import React, { useEffect, useState } from "react";
import { Sparkles, CalendarClock, Mail, Target, Image as ImageIcon, Loader2, Activity } from "lucide-react";
import { getActivityFeed } from "../../api";

const ICON = { sparkles: Sparkles, calendar: CalendarClock, mail: Mail, target: Target, image: ImageIcon };
const TINT = { ai_draft: "#b8935f", scheduled_post: "#8b5cf6", campaign: "#0ea5e9", prospect: "#16a34a", photo: "#e1306c" };
const STATUS = { pending: "bg-amber-100 text-amber-700", approved: "bg-green-100 text-green-700", scheduled: "bg-purple-100 text-purple-700", sent: "bg-blue-100 text-blue-700", queued: "bg-gray-100 text-gray-600", stored: "bg-gray-100 text-gray-600", published: "bg-green-100 text-green-700", identified: "bg-green-100 text-green-700" };
const ago = (d) => { const s = (Date.now() - new Date(d)) / 1000; if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; };

const ActivityFeed = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => { getActivityFeed().then(setData).catch(() => setData(null)).finally(() => setBusy(false)); }, []);

  if (busy) return <div className="flex items-center justify-center py-24 text-[#8a8a8a]"><Loader2 className="animate-spin mr-2" size={18} /> Loading activity…</div>;
  if (!data) return <div className="bg-white rounded-2xl border border-[#f0ece5] p-8 text-center text-[#8a8a8a]">No activity yet.</div>;

  const c = data.counts || {};
  const cards = [["ai_draft", "AI drafts"], ["scheduled_post", "Scheduled"], ["campaign", "Campaigns"], ["prospect", "Prospects"], ["photo", "Photos"]];
  const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const upcoming = (data.events || [])
    .filter((e) => e.type === "scheduled_post" && e.status === "scheduled")
    .sort((a, b) => String(a.at).localeCompare(String(b.at)));

  return (
    <div data-testid="activity-feed">
      <div className="flex items-center gap-2 mb-5">
        <Activity size={18} className="text-[#b8935f]" />
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b]">Business activity</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {cards.map(([k, l]) => (
          <div key={k} className="bg-white rounded-xl border border-[#f0ece5] p-4 text-center">
            <p className="font-display text-[24px] font-500" style={{ color: TINT[k] }}>{c[k] || 0}</p>
            <p className="text-[11px] text-[#8a8a8a] uppercase tracking-wide">{l}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-[#f0ece5] p-5 mb-6" data-testid="upcoming-scheduled">
        <h3 className="font-display text-[17px] font-500 text-[#2b2b2b] mb-3 flex items-center gap-2"><CalendarClock size={16} className="text-[#8b5cf6]" /> Upcoming scheduled posts ({upcoming.length})</h3>
        {upcoming.length === 0 ? (
          <p className="text-[13px] text-[#a0a0a0]">Nothing scheduled yet — use “Schedule best time” on a draft or “Schedule the whole week” on the Calendar.</p>
        ) : (
          <div className="space-y-2">
            {upcoming.map((e) => (
              <div key={`up-${e.id}`} className="flex items-center gap-3 p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]" data-testid="upcoming-item">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#8b5cf618" }}><CalendarClock size={15} style={{ color: "#8b5cf6" }} /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-500 text-[#2b2b2b] truncate">{e.title}</p>
                  {e.detail && <p className="text-[12px] text-[#8a8a8a] truncate">{e.detail}</p>}
                </div>
                <span className="text-[12px] text-[#8b5cf6] font-500 shrink-0">{fmtWhen(e.at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#f0ece5] divide-y divide-[#f5f1ea]" data-testid="activity-list">
        {data.events.map((e) => {
          const Icon = ICON[e.icon] || Sparkles;
          return (
            <div key={`${e.type}-${e.id}`} className="flex items-start gap-3 p-4">
              <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${TINT[e.type]}18` }}><Icon size={16} style={{ color: TINT[e.type] }} /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[14px] font-500 text-[#2b2b2b]">{e.title}</p>
                  {e.status && <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${STATUS[e.status] || "bg-gray-100 text-gray-600"}`}>{e.status}</span>}
                </div>
                {e.detail && <p className="text-[12px] text-[#8a8a8a] truncate">{e.detail}</p>}
              </div>
              <span className="text-[11px] text-[#b0b0b0] shrink-0">{ago(e.at)}</span>
            </div>
          );
        })}
        {data.events.length === 0 && <p className="text-[13px] text-[#c0c0c0] p-8 text-center">Nothing yet — generate content or schedule a post to see it here.</p>}
      </div>
    </div>
  );
};

export default ActivityFeed;
